import i18n, { TOptions } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import moment from "moment";
import "moment/locale/de";
import "moment/locale/en-gb";
import Log from "./debug/Log";
import { SET_UI_CONFIG } from "./redux/actions/ui-config/ui-config-actions-types";
import { DefaultUIConfigs } from "./redux/reducers/ui-config/UiConfig";
import { store } from "./redux/store";
import DataBusDefaults from "./services/DataBusDefaults";
import OverwriteService from "./services/OverwriteService";
import { Properties } from "./utils/Properties";
import StorageUtils from "./utils/StorageUtils";

// Constants
const FIXED_NAMESPACES_TO_LOAD = ["common", "at", "dashboard", "ra"] as const;
const LSTORAGE_SELECTED_LANGUAGE = "selectedLanguage";
const DEFAULT_LANGUAGE = "de";
const FALLBACK_LANGUAGE = "en";

// Types
interface TranslationOverwriteOptions {
  language: string;
  type: string | null;
}

interface LanguagePackageOptions {
  namespace?: string;
  forceReload?: boolean;
}

// Configuration
const getStoredLanguage = (): string => {
  try {
    return (
      (StorageUtils.LocalStorage.get(LSTORAGE_SELECTED_LANGUAGE) as string) ||
      DEFAULT_LANGUAGE
    );
  } catch (error) {
    Log.error("Failed to get stored language:", error);
    return DEFAULT_LANGUAGE;
  }
};

export const i18nOptions = {
  lng: getStoredLanguage(),
  fallbackLng: FALLBACK_LANGUAGE,
  debug: process.env.NODE_ENV === "development",
  saveMissing: false,
  defaultNS: "common",
  interpolation: {
    escapeValue: false, // React already protects from XSS
  },
  react: {
    wait: true,
    useSuspense: false,
  },
  detection: {
    order: ["localStorage", "navigator"],
    caches: ["localStorage"],
  },
} as const;

// Initialize i18n
i18n.use(LanguageDetector);

// Store original translate function
const originalTranslate = i18n.t.bind(i18n);
(i18n as any).translate = originalTranslate;

// Translation cache for debug mode
const translationsDone: Record<string, string> = {};

// Enhanced translation function with overwrite support
i18n.t = function (
  key: string,
  defaultValue?: TOptions<any> | string,
  options?: (TOptions<any> & { unit?: string }) | string
): string {
  try {
    const useOptions = defaultValue
      ? typeof defaultValue === "object"
        ? defaultValue
        : options
      : undefined;

    // Check for overwrites
    const overwrite = OverwriteService.getTranslation(key, {
      language: useOptions?.lng || i18n.language,
      type: useOptions?.unit || null,
    } as TranslationOverwriteOptions);

    let result: string;

    if (overwrite) {
      result = overwrite;
    } else if (
      key &&
      key.includes(":") &&
      useOptions?.ns &&
      useOptions.ns !== "common" &&
      useOptions?.ns?.[0] !== "common"
    ) {
      // Handle namespaced keys
      const namespaceKey = key.split(":")[1];
      result = originalTranslate(namespaceKey, defaultValue, useOptions);
    } else {
      result = originalTranslate(key, defaultValue, options);
    }

    // Debug logging
    if (Properties.translateDebug && !translationsDone[key]) {
      translationsDone[key] = result;
      Log.info("## Translation:", key, "=", result);
    }

    return result;
  } catch (error) {
    Log.error("Translation error for key:", key, error);
    return key; // Return key as fallback
  }
};

// Language package loading with better error handling
export const loadLanguagePackageByLanguage = async (
  language: string,
  namespace: string = "common"
): Promise<void> => {
  try {
    const response = await fetch(`/locales/${language}/${namespace}.json`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const useNamespace = namespace.includes("/")
      ? namespace.split("/")[1]
      : namespace;

    const languageLoaded = await response.json();

    // Validate JSON structure
    if (typeof languageLoaded !== "object" || languageLoaded === null) {
      throw new Error("Invalid JSON structure for language package");
    }

    i18n.addResourceBundle(language, useNamespace, languageLoaded, true, true);

    Log.info(`Language package loaded: ${language}/${useNamespace}`);
  } catch (error) {
    Log.error(
      `Failed to load language package ${language}/${namespace}:`,
      error
    );

    // Show user-friendly error message
    DataBusDefaults.toast({
      type: "error",
      text: `Error loading language package: ${namespace}`,
    });

    throw error; // Re-throw for calling code to handle
  }
};

// Load language package for current language
export const loadLanguagePackage = async (namespace: string): Promise<void> => {
  await loadLanguagePackageByLanguage(i18n.language, namespace);
};

// Set language with comprehensive initialization
export const setLanguage = async (language: string): Promise<void> => {
  try {
    Log.info(`Setting language to: ${language}`);

    // Update Redux store
    store.dispatch({
      type: SET_UI_CONFIG,
      key: DefaultUIConfigs.SELECTED_LANGUAGE,
      value: language,
    });

    // Update moment locale
    moment.locale(language);

    // Load all required namespaces
    const loadPromises = FIXED_NAMESPACES_TO_LOAD.map((namespace) =>
      loadLanguagePackageByLanguage(language, namespace)
    );

    await Promise.allSettled(loadPromises);

    // Change i18n language
    await i18n.changeLanguage(language);

    // Update HTML lang attribute
    const htmlElement = document.querySelector("html");
    if (htmlElement) {
      htmlElement.setAttribute("lang", language);
    }

    // Store language preference
    try {
      StorageUtils.LocalStorage.save(LSTORAGE_SELECTED_LANGUAGE, language);
    } catch (error) {
      Log.error("Failed to save language preference:", error);
    }

    Log.info(`Language set successfully: ${language}`);
  } catch (error) {
    Log.error(`Failed to set language to ${language}:`, error);
    throw error;
  }
};

// Change language with page reload (for complete language switch)
export const changeLanguage = async (language: string): Promise<void> => {
  try {
    Log.info(`Changing language to: ${language}`);

    // Update Redux store
    store.dispatch({
      type: SET_UI_CONFIG,
      key: DefaultUIConfigs.SELECTED_LANGUAGE,
      value: language,
    });

    // Store language preference
    try {
      StorageUtils.LocalStorage.save(LSTORAGE_SELECTED_LANGUAGE, language);
    } catch (error) {
      Log.error("Failed to save language preference:", error);
    }

    // Reload page for complete language switch
    window.location.reload();
  } catch (error) {
    Log.error(`Failed to change language to ${language}:`, error);
    throw error;
  }
};

// Get available languages
export const getAvailableLanguages = (): string[] => {
  try {
    return Object.keys((i18n as any).store?.data || {});
  } catch (error) {
    Log.error("Failed to get available languages:", error);
    return [DEFAULT_LANGUAGE, FALLBACK_LANGUAGE];
  }
};

// Get current language
export const getCurrentLanguage = (): string => {
  return i18n.language || DEFAULT_LANGUAGE;
};

// Check if language is loaded
export const isLanguageLoaded = (
  language: string,
  namespace?: string
): boolean => {
  try {
    const store = (i18n as any).store;
    if (!store?.data?.[language]) {
      return false;
    }

    if (namespace) {
      return !!store.data[language][namespace];
    }

    return true;
  } catch (error) {
    Log.error("Failed to check if language is loaded:", error);
    return false;
  }
};

// Preload language packages
export const preloadLanguage = async (language: string): Promise<void> => {
  try {
    Log.info(`Preloading language: ${language}`);

    const loadPromises = FIXED_NAMESPACES_TO_LOAD.map((namespace) =>
      loadLanguagePackageByLanguage(language, namespace)
    );

    await Promise.allSettled(loadPromises);
    Log.info(`Language preloaded successfully: ${language}`);
  } catch (error) {
    Log.error(`Failed to preload language ${language}:`, error);
    throw error;
  }
};

// Initialize with stored language
const initializeLanguage = async (): Promise<void> => {
  try {
    const storedLanguage = getStoredLanguage();
    await setLanguage(storedLanguage);
  } catch (error) {
    Log.error("Failed to initialize language:", error);
    // Fallback to default language
    await setLanguage(DEFAULT_LANGUAGE);
  }
};

// Initialize language on module load
initializeLanguage().catch((error) => {
  Log.error("Critical error during language initialization:", error);
});

export default i18n;
